import {
    canonicalizeCalculatorFishingSourceV1,
} from "./calculator-fishing-source.js";

const STAT_TERM_SUBJECT_KINDS = new Set([
    "none",
    "item",
    "option",
    "petOption",
    "fishingSource",
    "fishingLevel",
    "lifeskillLevel",
    "resources",
    "mastery",
    "brand",
]);
const PET_OPTION_KINDS = new Set(["special", "talent", "learnedSkill"]);
const RESOURCE_ABUNDANCE_STATES = new Set([
    "exhausted",
    "low",
    "average",
    "abundant",
]);

/**
 * Creates the strict parser for the shared CalculatorStatTermSubjectV1 wire
 * contract. Consumers provide their local error and primitive-validation
 * policy while this module remains the sole owner of variants and fields.
 */
export function createCalculatorStatTermSubjectParser(adapter) {
    const {
        fail,
        requireBoolean,
        requireExactFields,
        requireFinite,
        requireInteger,
        requireRecord,
        requireString,
    } = adapter;

    return function parseCalculatorStatTermSubject(value, path) {
        const subject = requireRecord(value, path);
        const kind = requireString(subject.kind, `${path}.kind`);
        if (!STAT_TERM_SUBJECT_KINDS.has(kind)) {
            fail(`${path}.kind`, "unsupported stat term subject");
        }
        if (kind === "none") {
            requireExactFields(subject, ["kind"], path);
            return { kind };
        }
        if (kind === "item") {
            const record = requireExactFields(subject, ["itemId", "kind"], path);
            return {
                kind,
                itemId: requireInteger(record.itemId, `${path}.itemId`, { minimum: 1 }),
            };
        }
        if (kind === "option") {
            const record = requireExactFields(subject, ["kind", "optionId"], path);
            return {
                kind,
                optionId: requireString(record.optionId, `${path}.optionId`),
            };
        }
        if (kind === "petOption") {
            const record = requireExactFields(
                subject,
                ["kind", "optionId", "optionKind", "packLeader", "petId", "slot", "tier"],
                path,
            );
            const optionKind = requireString(record.optionKind, `${path}.optionKind`);
            if (!PET_OPTION_KINDS.has(optionKind)) {
                fail(`${path}.optionKind`, "unsupported pet option kind");
            }
            return {
                kind,
                petId: requireString(record.petId, `${path}.petId`),
                tier: requireInteger(record.tier, `${path}.tier`, { minimum: 1 }),
                optionId: requireString(record.optionId, `${path}.optionId`),
                optionKind,
                slot: requireInteger(record.slot, `${path}.slot`, { minimum: 1 }),
                packLeader: requireBoolean(record.packLeader, `${path}.packLeader`),
            };
        }
        if (kind === "fishingSource") {
            const record = requireExactFields(subject, ["fishingSource", "kind"], path);
            return {
                kind,
                fishingSource: canonicalizeCalculatorFishingSourceV1(
                    record.fishingSource,
                    `${path}.fishingSource`,
                    (issuePath, _code, message) => fail(issuePath, message),
                ),
            };
        }
        if (kind === "fishingLevel" || kind === "lifeskillLevel") {
            const record = requireExactFields(subject, ["kind", "level"], path);
            return {
                kind,
                level: requireInteger(record.level, `${path}.level`),
            };
        }
        if (kind === "resources") {
            const record = requireExactFields(subject, ["abundance", "kind", "percent"], path);
            const abundance = requireString(record.abundance, `${path}.abundance`);
            if (!RESOURCE_ABUNDANCE_STATES.has(abundance)) {
                fail(`${path}.abundance`, "unsupported abundance state");
            }
            return {
                kind,
                percent: requireFinite(
                    record.percent,
                    `${path}.percent`,
                    { minimum: 0, maximum: 100 },
                ),
                abundance,
            };
        }
        if (kind === "mastery") {
            const record = requireExactFields(subject, ["kind", "mastery"], path);
            return {
                kind,
                mastery: requireInteger(record.mastery, `${path}.mastery`),
            };
        }
        const record = requireExactFields(subject, ["active", "kind"], path);
        return {
            kind,
            active: requireBoolean(record.active, `${path}.active`),
        };
    };
}
